import React, { FC, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { TDA_LINK, ROBERTS_RULES_LINK, APP_INFO_EMAIL } from '../../constants/links.constants';
import {
  Button,
  Flex,
  Box,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Avatar,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import DataService from '../../services/DataService.ts';
import NavItem from '../NavItem/NavItem';
import Logo from '../Logo/Logo';
import NotificationBell from '../NotificationBell/NotificationBell';
import { UserAuth } from '../../AuthContext';
import { db } from '../../firebase';
import { doc, getDoc } from 'firebase/firestore';
import {
  IsSmallOrLessDisplay,
  ShowBreakpointAsText,
} from '../../utils/display';
import { SIZES } from '../../assets/themes/base/sizes';
import { PX_APP_LAYOUT, Z_INDEX } from '../../assets/themes/base/space';
interface NavBarProps {}

const HelpMenuItem = () => {
  return (
    <Flex
      direction='column'
      align={'center'}
      justify='center'
      color='text.primary'
    >
      <Text fontSize='sm'>Need Assistance?</Text>
      <Text fontSize='sm'>Email us at</Text>
      <Button variant='link' size='sm'>
        <Link to='mailto:info@pokercity.com'>{APP_INFO_EMAIL}</Link>
      </Button>
    </Flex>
  );
};

const MenuUserInfo = (props: any) => {
  const { user } = props;
  return (
    <MenuItem
      color='text.primary'
      _focus={{ bg: 'charcoal.50', cursor: 'default' }}
    >
      <Flex
        align='center'
        justify='center'
        direction='column'
        gap={2}
        p={1}
        w='100%'
      >
        <Avatar size='lg' src={user?.photoURL} referrerPolicy='no-referrer' />
        <Text>{user.displayName}</Text>
      </Flex>
    </MenuItem>
  );
};

const NavMenuButton: FC<{ user: any; isSmallDisplay: boolean }> = (props: {
  isSmallDisplay: boolean;
  user: any;
}) => {
  const { logOut, googleSignIn } = UserAuth();
  const { user, isSmallDisplay } = props;
  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = React.useRef<HTMLDivElement>(null);

  // Close on route change
  React.useEffect(() => { setOpen(false); }, [location.pathname]);

  // Close on outside tap/click
  React.useEffect(() => {
    const handler = (e: MouseEvent | TouchEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handler);
      document.addEventListener('touchstart', handler);
    }
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [open]);

  const go = (path: string) => { setOpen(false); navigate(path); };
  const itemStyle: React.CSSProperties = { display: 'block', width: '100%', padding: '10px 16px', background: 'none', border: 'none', textAlign: 'left', fontSize: '14px', cursor: 'pointer', color: '#1A202C', fontFamily: 'inherit' };
  const divStyle: React.CSSProperties = { height: 1, background: '#E2E8F0', margin: '4px 0' };

  return (
    <Flex align='center' justify='center' direction='row' gap={2}>
      {user && <NotificationBell />}
      {user && (
        <div ref={menuRef} style={{ position: 'relative' }}>
          <button
            onClick={() => setOpen(o => !o)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            aria-label='Menu'
          >
            <Avatar size='sm' src={user?.photoURL} referrerPolicy='no-referrer' />
          </button>

          {open && (
            <div style={{ position: 'absolute', right: 0, top: '110%', background: 'white', border: '1px solid #E2E8F0', borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.15)', minWidth: 200, zIndex: 9999, overflow: 'hidden' }}>
              {/* User info */}
              <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                <Avatar size='lg' src={user?.photoURL} referrerPolicy='no-referrer' />
                <span style={{ fontSize: 14, color: '#1A202C', fontWeight: 500 }}>{user.displayName}</span>
              </div>

              {isSmallDisplay && (
                <>
                  <div style={divStyle} />
                  <button style={itemStyle} onMouseDown={() => go('/dashboard')} onTouchEnd={(e) => { e.preventDefault(); go('/dashboard'); }}>Dashboard</button>
                  <button style={itemStyle} onMouseDown={() => go(user ? `/passport/${user.uid}` : '/dashboard')} onTouchEnd={(e) => { e.preventDefault(); go(user ? `/passport/${user.uid}` : '/dashboard'); }}>Passport™</button>
                  <button style={itemStyle} onMouseDown={() => go('/certification')} onTouchEnd={(e) => { e.preventDefault(); go('/certification'); }}>Rules Cert</button>
                  <button style={itemStyle} onMouseDown={() => go('/find-a-game')} onTouchEnd={(e) => { e.preventDefault(); go('/find-a-game'); }}>Find a Game</button>
                  <button style={itemStyle} onMouseDown={() => go('/td')} onTouchEnd={(e) => { e.preventDefault(); go('/td'); }}>TD Control Center</button>
                  <button style={itemStyle} onMouseDown={() => go('/pricing')} onTouchEnd={(e) => { e.preventDefault(); go('/pricing'); }}>Pricing</button>
                  <button style={itemStyle} onMouseDown={() => go('/help')} onTouchEnd={(e) => { e.preventDefault(); go('/help'); }}>Help</button>
                  <button style={itemStyle} onMouseDown={() => go('/create-venue')} onTouchEnd={(e) => { e.preventDefault(); go('/create-venue'); }}>Create Venue</button>
                  <button style={itemStyle} onMouseDown={() => go('/poster-generator')} onTouchEnd={(e) => { e.preventDefault(); go('/poster-generator'); }}>Poster Generator</button>
                  <button style={itemStyle} onMouseDown={() => go('/find-a-dealer')} onTouchEnd={(e) => { e.preventDefault(); go('/find-a-dealer'); }}>Find a Dealer</button>
                  <button style={itemStyle} onMouseDown={() => { setOpen(false); window.open(TDA_LINK, '_blank'); }} onTouchEnd={(e) => { e.preventDefault(); setOpen(false); window.open(TDA_LINK, '_blank'); }}>Poker TDA Rules</button>
                  <button style={itemStyle} onMouseDown={() => { setOpen(false); window.open(ROBERTS_RULES_LINK, '_blank'); }} onTouchEnd={(e) => { e.preventDefault(); setOpen(false); window.open(ROBERTS_RULES_LINK, '_blank'); }}>Robert's Rules</button>
                </>
              )}

              <div style={divStyle} />
              <div style={{ padding: '8px 16px', textAlign: 'center' }}>
                <span style={{ fontSize: 13, color: '#718096' }}>Need Assistance?</span><br />
                <span style={{ fontSize: 13, color: '#718096' }}>Email us at</span><br />
                <a href='mailto:info@pokercity.com' style={{ fontSize: 13, color: '#ED8936', fontWeight: 600 }}>INFO@POKERCITY.COM</a>
              </div>
              <div style={divStyle} />
              <button style={{ ...itemStyle, color: '#E53E3E', textAlign: 'center', fontWeight: 600 }} onMouseDown={() => { setOpen(false); logOut(); }} onTouchEnd={(e) => { e.preventDefault(); setOpen(false); logOut(); }}>Sign Out</button>
            </div>
          )}
        </div>
      )}

      {!user && (
        <Flex direction='column'>
          <Button variant='primary' onClick={async () => googleSignIn()}>Sign In</Button>
        </Flex>
      )}
    </Flex>
  );
};

const NavMenu: FC<{
  user: any;
  isSmallDisplay: boolean;
  pathname: string;
}> = (props: { isSmallDisplay: boolean; user: any; pathname: string }) => {
  const { isSmallDisplay } = props;
  const [isDealer, setIsDealer] = React.useState(false);
  React.useEffect(() => {
    if (!props.user?.uid) return;
    getDoc(doc(db, 'dealers', props.user.uid)).then(snap => {
      if (snap.exists() && snap.data().tier && snap.data().tier !== 'none') setIsDealer(true);
    }).catch(() => {});
  }, [props.user?.uid]);
  const dashboardPath = '/dashboard';
  return (
    <>
      {!isSmallDisplay && (
        <Flex align={'center'} justify={'center'} direction={'row'} gap={1}>
          <NavItem to='/dashboard' selected={props.pathname === dashboardPath}>
            Dashboard
          </NavItem>
          <NavItem to={props.user ? `/passport/${props.user.uid}` : '/dashboard'} selected={props.pathname.startsWith('/passport')}>
            Passport™
          </NavItem>
          <NavItem to='/certification' selected={props.pathname.startsWith('/certification')}>
            Rules Cert
          </NavItem>
          {isDealer && (
            <NavItem to={props.user ? `/dealer/${props.user.uid}` : '/dashboard'} selected={props.pathname.startsWith('/dealer/')}>
              Dealer Passport
            </NavItem>
          )}
          <NavItem to='/find-a-game' selected={props.pathname.startsWith('/find-a-game')}>
            Find a Game
          </NavItem>
          <NavItem to='/find-a-dealer' selected={props.pathname.startsWith('/find-a-dealer')}>
            Find a Dealer
          </NavItem>
          <NavItem externalLink={TDA_LINK}>Poker TDA Rules </NavItem>
          <NavItem externalLink={ROBERTS_RULES_LINK}>Robert's Rules </NavItem>
        </Flex>
      )}
    </>
  );
};

const NavBar: FC<NavBarProps> = (props: NavBarProps) => {
  const { user } = UserAuth();
  const isSmallDisplay = IsSmallOrLessDisplay();
  const location = useLocation();
  const { pathname } = location;
  useEffect(() => {
    const checkUserData = async () => {
      if (user !== null && user !== undefined && user.uid) {
        try {
          const userData = await DataService.getUserData(user.uid);
          if (
            !userData ||
            userData.display_name !== user.displayName ||
            userData.avatar_url !== user.photoURL ||
            userData.email !== user.email
          ) {
            // User data needs to be updated
            await DataService.setUserData(
              user.uid,
              user.displayName as string,
              user.photoURL as string,
              user.email as string
            );
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          // Handle the error appropriately
        }
      }
    };

    checkUserData();
  }, [user]);
  return (
    <Flex
      as='nav'
      align='center'
      justify='space-between'
      wrap='wrap'
      w='100%'
      maxW={SIZES.MAX_W_PAGE}
      mx='auto'
      bg='white'
      px={PX_APP_LAYOUT}
      {...props}
    >
      <Flex gap={6}>
        <Flex align='center' justify='center'>
          <Box as={Link} to='/'>
            <Logo w='125px' h='auto' />
          </Box>
        </Flex>

        {user && (
          <NavMenu
            user={user}
            isSmallDisplay={isSmallDisplay}
            pathname={pathname}
          ></NavMenu>
        )}
      </Flex>
      <NavMenuButton
        user={user}
        isSmallDisplay={isSmallDisplay}
      ></NavMenuButton>
    </Flex>
  );
};

export default NavBar;
